if (!customElements.get('theme-drawer')) {
  class ThemeDrawer extends HTMLElement {
    constructor() {
      super();
      this.overlay = null;
      this.panel = null;
      this.previousActiveElement = null;
      this.focusableElements = null;
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleOverlayClick = this.handleOverlayClick.bind(this);
    }

    connectedCallback() {
      this.overlay = this.querySelector('[data-drawer-overlay]');
      this.panel = this.querySelector('[data-drawer-panel]');
    }

    disconnectedCallback() {
      this._removeListeners();
    }

    get side() {
      return this.dataset.side || 'right';
    }

    get trapFocus() {
      return this.dataset.trapFocus !== 'false';
    }

    get closeOnEsc() {
      return this.dataset.closeOnEsc !== 'false';
    }

    get closeOnOverlayClick() {
      return this.dataset.closeOnOverlayClick !== 'false';
    }

    open() {
      this.previousActiveElement = document.activeElement;
      this.setAttribute('data-open', '');
      this._updateFocusableElements();
      this._addListeners();

      requestAnimationFrame(() => {
        this.focusableElements?.[0]?.focus();
      });

      this._publish(theme?.pubsub?.events?.DRAWER_OPEN);
    }

    close() {
      this.removeAttribute('data-open');
      this._removeListeners();

      this.previousActiveElement?.focus();
      this.previousActiveElement = null;

      this._publish(theme?.pubsub?.events?.DRAWER_CLOSE);
    }

    toggle() {
      this.hasAttribute('data-open') ? this.close() : this.open();
    }

    _addListeners() {
      document.addEventListener('keydown', this.handleKeyDown);
      if (this.closeOnOverlayClick) {
        this.overlay?.addEventListener('click', this.handleOverlayClick);
      }
    }

    _removeListeners() {
      document.removeEventListener('keydown', this.handleKeyDown);
      this.overlay?.removeEventListener('click', this.handleOverlayClick);
    }

    handleKeyDown(e) {
      if (e.key === 'Escape' && this.closeOnEsc) {
        this.close();
        return;
      }

      if (e.key === 'Tab' && this.trapFocus) {
        this._handleTabKey(e);
      }
    }

    handleOverlayClick() {
      this.close();
    }

    _handleTabKey(e) {
      const first = this.focusableElements?.[0];
      const last = this.focusableElements?.[this.focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        last?.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first?.focus();
        e.preventDefault();
      }
    }

    refreshFocus() {
      this._updateFocusableElements();
    }

    _updateFocusableElements() {
      const selector = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
      this.focusableElements = [...(this.panel ?? this).querySelectorAll(selector)].filter(
        el => !el.hidden && !el.disabled && el.offsetParent !== null
      );
    }

    _publish(eventName) {
      if (!eventName || typeof theme === 'undefined' || !theme.pubsub) return;
      theme.pubsub.publish(eventName, { id: this.id });
    }
  }

  customElements.define('theme-drawer', ThemeDrawer);
}
